// Revisado: 2025-12-24 [I18n Scan]
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import SupportList from './SupportList';
import CloudDecks from './CloudDecks'; // Import CloudDecks
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../../constants';

const SLOT_COLORS = [
    { bg: 'bg-red-500', shadow: 'shadow-[0_0_8px_rgba(239,68,68,0.8)]' },
    { bg: 'bg-blue-500', shadow: 'shadow-[0_0_8px_rgba(59,130,246,0.8)]' },
    { bg: 'bg-emerald-500', shadow: 'shadow-[0_0_8px_rgba(16,185,129,0.8)]' },
    { bg: 'bg-orange-500', shadow: 'shadow-[0_0_8px_rgba(249,115,22,0.8)]' },
    { bg: 'bg-violet-500', shadow: 'shadow-[0_0_8px_rgba(139,92,246,0.8)]' },
    { bg: 'bg-cyan-500', shadow: 'shadow-[0_0_8px_rgba(6,182,212,0.8)]' }
];

const SupportCards = ({ slots, setSlots, data }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSlotIndex, setCurrentSlotIndex] = useState(null);
    const [isExpanded, setIsExpanded] = useState(true);

    // Derive Data for CloudDecks (ID + Cap)
    const currentDeckData = slots.map(slot => slot.support ? { id: slot.support.support_id, cap: slot.capLevel } : null);

    // Helper to load deck from Cloud
    const handleLoadDeck = (savedDeckData) => {
        if (!Array.isArray(savedDeckData) || !data) return;

        const newSlots = savedDeckData.map(item => {
            // Handle legacy format (simple array of IDs) or new format (objects)
            let id = item;
            let cap = 0;

            if (typeof item === 'object' && item !== null) {
                id = item.id;
                cap = item.cap || 0;
            }

            if (!id) return { support: null, capLevel: 0 };

            // Find card in data (data.matrix is where cards are)
            const matrix = data.matrix || {};

            // Try direct lookup if keys are IDs (likely string matches)
            let found = matrix[String(id)] || matrix[Number(id)];

            // Fallback to array search if direct lookup failed
            if (!found) {
                found = Object.values(matrix).find(c => c.support_id === Number(id));
            }

            if (found) {
                // IMPORTANT: SupportList flattens the object ({...info, support_id}). 
                // We must do the same here so the UI can read properties like .char_name or .support_id directly.
                const flattened = {
                    ...found.info,
                    support_id: found.info?.support_id || Number(id)
                };
                return {
                    support: flattened,
                    capLevel: cap
                };
            }
            return { support: null, capLevel: 0 };
        });

        // Ensure 6 slots
        while (newSlots.length < 6) {
            newSlots.push({ support: null, capLevel: 0 });
        }

        setSlots(newSlots);
    };

    const handleSlotClick = (index) => {
        if (!data) return; // Prevent opening if data not loaded
        setCurrentSlotIndex(index);
        setIsModalOpen(true);
    };

    const handleSupportSelect = (support) => {
        const newSlots = [...slots];
        newSlots[currentSlotIndex] = {
            support: support,
            capLevel: 0 // Default to 0 based on user history preference
        };
        // However, for a comparator/builder, usually you want to see max stats.
        // I will default to 4 for convenience, or 0 if strictly following selector.
        // Let's default to 4 (Max) as it's more useful for "Builder", 
        // but the prompt says "Igual que en... SupportSelector.js". 
        // SupportSelector defaults to 0. I will respect the prompt strictness.
        newSlots[currentSlotIndex].capLevel = 0;

        setSlots(newSlots);
        setIsModalOpen(false);
    };

    const handleRightClick = (e, index) => {
        e.preventDefault();
        const newSlots = [...slots];
        newSlots[index] = { support: null, capLevel: 0 };
        setSlots(newSlots);
    };

    const handleCapClick = (e, slotIndex, capIndex) => {
        e.stopPropagation();
        const currentSlot = slots[slotIndex];
        if (!currentSlot.support) return;

        const clickedLevel = capIndex + 1; // 0th crystal -> Level 1 (Cap 1)
        let newLevel = clickedLevel;

        // Toggle logic from SupportSelector.js
        if (currentSlot.capLevel === 1 && clickedLevel === 1) {
            newLevel = 0;
        }

        const newSlots = [...slots];
        newSlots[slotIndex] = { ...currentSlot, capLevel: newLevel };
        setSlots(newSlots);
    };

    const getTypeIconPath = (supportType) => {
        if (!supportType) return '';
        const type = supportType.charAt(0).toUpperCase() + supportType.slice(1).toLowerCase();
        if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
            return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
        } else if (['Pal', 'Group'].includes(type)) {
            return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
        }
        return '';
    };

    return (
        <div className="w-full bg-white dark:bg-slate-800 rounded-xl shadow-sm mb-6 overflow-hidden border border-slate-200 dark:border-slate-700 transition-all duration-300">
            {/* Header */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
                <div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">{t('SupportCards.title')}</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t('SupportCards.selected_count', { count: slots.filter(s => s.support !== null).length })}
                    </p>
                </div>
                <svg
                    className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {isExpanded && <div className="h-px bg-slate-200 dark:bg-slate-700 mx-4"></div>}

            <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                <div className="p-4">

                    {/* Cloud Decks Manager */}
                    <CloudDecks
                        currentDeckData={currentDeckData}
                        onLoadDeck={handleLoadDeck}
                    />

                    <div className="flex flex-wrap justify-center gap-4">
                        {slots.map((slot, index) => {
                            const support = slot.support;
                            const cardImagePath = support
                                ? `${API_BASE_URL}/api/thumb?img=/images/Support/Card/Support_Card_${support.support_id}_Card.png&width=160`
                                : null;
                            const typeIconPath = support ? getTypeIconPath(support.Support_Type) : null;
                            const slotColor = SLOT_COLORS[index % SLOT_COLORS.length];

                            return (
                                <div key={index} className="flex flex-col items-center shrink-0">

                                    {/* LED Indicator */}
                                    <div className={`w-3 h-1.5 rounded-full mb-2 transition-all duration-500 
                                            ${support ? `${slotColor.bg} ${slotColor.shadow}` : 'bg-slate-300 dark:bg-slate-700'}`}
                                    ></div>

                                    {/* CARD SLOT */}
                                    <div
                                        onClick={() => handleSlotClick(index)}
                                        onContextMenu={(e) => handleRightClick(e, index)}
                                        className="relative cursor-pointer transition-transform hover:scale-105 group mb-2"
                                        style={{
                                            width: '100px',
                                            height: '133px',
                                            backgroundColor: 'rgb(247, 248, 247)',
                                            border: '2px solid rgb(217, 216, 217)',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            boxShadow: support ? 'none' : 'inset 0px 12px 12px -6px rgba(0,0,0,0.08), inset 6px 0px 8px -4px rgba(0,0,0,0.05), inset -6px 0px 8px -4px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {support ? (
                                            <>
                                                <img
                                                    src={cardImagePath}
                                                    alt={support.char_name}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                                {typeIconPath && (
                                                    <div className="absolute bottom-1 right-1 bg-black/40 rounded-full p-0.5 backdrop-blur-sm">
                                                        <img src={typeIconPath} className="w-5 h-5 object-contain" alt={support.Support_Type} />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <svg width="40" height="40" viewBox="0 0 50 50" className="select-none transition-transform group-hover:scale-110">
                                                    <defs>
                                                        <linearGradient id="builderPlusGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                                                            <stop offset="0%" stopColor="rgb(177, 225, 12)" />
                                                            <stop offset="100%" stopColor="rgb(129, 211, 33)" />
                                                        </linearGradient>
                                                    </defs>
                                                    <rect x="20" y="0" width="10" height="50" rx="3" fill="url(#builderPlusGradient)" />
                                                    <rect x="0" y="20" width="50" height="10" rx="3" fill="url(#builderPlusGradient)" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* CRYSTALS */}
                                    {support ? (
                                        <div className="flex gap-1 justify-center h-6 items-center bg-slate-50 dark:bg-slate-700/50 rounded-full px-2 border border-slate-200 dark:border-slate-600 shadow-sm">
                                            {[0, 1, 2, 3].map((capIndex) => {
                                                const isActive = (capIndex + 1) <= slot.capLevel;
                                                return (
                                                    <img
                                                        key={capIndex}
                                                        src="/images/Icon/Resources/CrystalCap.png"
                                                        alt={`Cap ${capIndex + 1}`}
                                                        onClick={(e) => handleCapClick(e, index, capIndex)}
                                                        className={`w-4 h-auto cursor-pointer transition-all hover:scale-110 ${isActive ? '' : 'grayscale opacity-30'}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="h-6 w-full"></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-4 text-xs text-slate-400 text-center">
                        {t('SupportSelector.card_instructions')}
                    </p>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && createPortal(
                <div onClick={() => setIsModalOpen(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-5xl w-full max-h-[90vh] flex flex-col relative animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">{t('SupportSelector.modal_title')}</h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <SupportList onSelect={handleSupportSelect} data={data} />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SupportCards;
